import { useEffect } from 'react';

interface PageMetaOptions {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogLocale?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterUrl?: string;
  canonicalUrl?: string;
}

interface OriginalMeta {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  twitterTitle: string;
  twitterDescription: string;
  canonicalUrl: string;
}

/**
 * Hook per gestire i meta tag SEO di una pagina
 * Imposta i meta tag all'mount e li ripristina all'unmount
 */
export const usePageMeta = (options: PageMetaOptions) => {
  useEffect(() => {
    // Salva i valori originali
    const original: OriginalMeta = {
      title: document.title,
      description: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
      ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
      twitterTitle: document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || '',
      twitterDescription: document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || '',
      canonicalUrl: document.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
    };

    // Imposta il title
    document.title = options.title;

    // Imposta meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', options.description);
    }

    // Imposta Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', options.ogTitle || options.title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', options.ogDescription || options.description);
    }

    if (options.ogUrl) {
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', options.ogUrl);
      }
    }

    if (options.ogImage) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.setAttribute('content', options.ogImage);
      }
    }

    // Imposta og:locale
    if (options.ogLocale) {
      let ogLocale = document.querySelector('meta[property="og:locale"]');
      if (ogLocale) {
        ogLocale.setAttribute('content', options.ogLocale);
      } else {
        ogLocale = document.createElement('meta');
        ogLocale.setAttribute('property', 'og:locale');
        ogLocale.setAttribute('content', options.ogLocale);
        document.head.appendChild(ogLocale);
      }
    }

    // Imposta og:image:width
    if (options.ogImageWidth) {
      let ogImageWidth = document.querySelector('meta[property="og:image:width"]');
      if (ogImageWidth) {
        ogImageWidth.setAttribute('content', options.ogImageWidth);
      } else {
        ogImageWidth = document.createElement('meta');
        ogImageWidth.setAttribute('property', 'og:image:width');
        ogImageWidth.setAttribute('content', options.ogImageWidth);
        document.head.appendChild(ogImageWidth);
      }
    }

    // Imposta og:image:height
    if (options.ogImageHeight) {
      let ogImageHeight = document.querySelector('meta[property="og:image:height"]');
      if (ogImageHeight) {
        ogImageHeight.setAttribute('content', options.ogImageHeight);
      } else {
        ogImageHeight = document.createElement('meta');
        ogImageHeight.setAttribute('property', 'og:image:height');
        ogImageHeight.setAttribute('content', options.ogImageHeight);
        document.head.appendChild(ogImageHeight);
      }
    }

    // Imposta Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', options.twitterTitle || options.ogTitle || options.title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', options.twitterDescription || options.ogDescription || options.description);
    }

    if (options.twitterImage) {
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) {
        twitterImage.setAttribute('content', options.twitterImage);
      }
    }

    // Imposta twitter:url
    if (options.twitterUrl) {
      let twitterUrl = document.querySelector('meta[name="twitter:url"]');
      if (twitterUrl) {
        twitterUrl.setAttribute('content', options.twitterUrl);
      } else {
        twitterUrl = document.createElement('meta');
        twitterUrl.setAttribute('name', 'twitter:url');
        twitterUrl.setAttribute('content', options.twitterUrl);
        document.head.appendChild(twitterUrl);
      }
    }

    // Imposta canonical URL
    if (options.canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', options.canonicalUrl);
      } else {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('href', options.canonicalUrl);
        document.head.appendChild(canonical);
      }
    }

    // Cleanup: ripristina i valori originali
    return () => {
      document.title = original.title;

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && original.description) {
        metaDescription.setAttribute('content', original.description);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle && original.ogTitle) {
        ogTitle.setAttribute('content', original.ogTitle);
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription && original.ogDescription) {
        ogDescription.setAttribute('content', original.ogDescription);
      }

      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl && original.ogUrl) {
        ogUrl.setAttribute('content', original.ogUrl);
      }

      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle && original.twitterTitle) {
        twitterTitle.setAttribute('content', original.twitterTitle);
      }

      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription && original.twitterDescription) {
        twitterDescription.setAttribute('content', original.twitterDescription);
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical && original.canonicalUrl) {
        canonical.setAttribute('href', original.canonicalUrl);
      }
    };
  }, [options]);
};
